import { DeliveryMethods, PaymentMethods } from '@/enums';
import { Cart } from '@/modules/cart/types';
import { BaseApiService } from '@/services/base-service';
import {
  BaseResponse, Filters, FiltersPagination, Response, TableRequest,
} from '@/types';
import { OrderStatuses } from '@/enums/statuses/orders';
import { PaymentsStatuses } from '@/enums/statuses/payments/';
import { ShipmentStatuses } from '@/enums/statuses/shipment';
import { Orders } from '../types';

export class OrdersService extends BaseApiService {
  private readonly base_path = 'orders';

  async send_order(order: Orders.Order): Promise<Response<Orders.CreateOrderResponse>> {
    const { data } = await this.http_client.post<Response<Orders.CreateOrderResponse>>(this.base_path, {
      project_id: order.project_id,
      organization_id: order.organization,
      payment_type: order.payment_type,
      delivery_type: order.delivery_type,
      warehouse_id: order.warehouse,
      products: order.products,
      delivery_address_id: order.delivery_address,
      transport_company_id: order.transport_company_id,
      self_delivery_type: order.self_delivery_type,
      tc_delivery_type: order.tc_delivery_type,
      tc_receiving_method: order.tc_receiving_method,
      is_express: order.is_express,
      is_dellin: order.is_dellin,
      delline_place: order.delline_place,
      delline_place_id: order.delline_place_id,
      delline_terminal: order.delline_terminal,
      delline_terminal_id: order.delline_terminal_id,
      comment: order.comment,
    });

    return data;
  }

  async get_orders(payload: TableRequest<Orders.RequestFilters>): Promise<Orders.Response> {
    const payload_filters = payload.filters;

    payload_filters.search = payload_filters.search ? payload_filters.search : '';

    const { data } = await this.http_client.get<Orders.Response>(this.base_path, {
      params: {
        filters: payload.filters,
        filters_exist: payload.filters_exist,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_order_by_id(id: string | number): Promise<Response<Orders.OrderDetailedInfo>> {
    const url = `${this.base_path}/${id}`;
    const { data } = await this.http_client.get<Response<Orders.OrderDetailedInfo>>(url);

    return data;
  }

  async get_cancel_reasons(): Promise<Orders.CancelReasonsList> {
    const url = `${this.base_path}/cancel_reasons`;

    const { data } = await this.http_client.get<Orders.CancelReasonsList>(url);

    return data;
  }

  async cancel_order(id: string | number, order_cancel_reason_id: number): Promise<void> {
    const url = `${this.base_path}/${id}/cancel`;
    const payload = {
      order_cancel_reason_id,
    };

    await this.http_client.patch(url, payload);
  }

  async get_order_documents(id: string | number): Promise<Response<Orders.OrderDocument[]>> {
    const url = `${this.base_path}/${id}/files`;
    const { data } = await this.http_client.get<Response<Orders.OrderDocument[]>>(url);

    return data;
  }

  async get_order_history_logs(id: string | number): Promise<Response<Orders.OrderHistoryLog[]>> {
    const url = `${this.base_path}/${id}/logs`;
    const { data } = await this.http_client.get<Response<Orders.OrderHistoryLog[]>>(url);

    return data;
  }

  async update_order_by_id(
    id: number,
    updated_items: { items: Orders.UpdatedOrderDetails[] },
  ): Promise<BaseResponse> {
    const url = `${this.base_path}/${id}/products`;
    const { data } = await this.http_client.patch<BaseResponse>(url, updated_items);

    return data;
  }

  async get_orders_invoices(filters: FiltersPagination<Filters>): Promise<Orders.OrdersInvoicesList> {
    const url = `${this.base_path}/invoices`;

    const payload = filters;

    const { data } = await this.http_client.get<Orders.OrdersInvoicesList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_info_from_last_order() {
    const url = 'cart/history/user-last'; // TODO файл для всех урлов нужно сделать
    const { data } = await this.http_client.get(url);

    return data;
  }

  async get_information_on_order_shipment(payload: Orders.OrderProductIds): Promise<Response<Orders.ShipmentInformation>> {
    const { data } = await this.http_client.get<Response<Orders.ShipmentInformation>>(`${this.base_path}/inactive_shipment_dates`, {
      params: {
        filters: payload,
      },
    });

    return data;
  }

  async post_information_on_order_shipment(payload: Orders.ShipmentOrdersDate): Promise<Response<Orders.ShipmentInformation>> {
    const { data } = await this.http_client.patch<Response<Orders.ShipmentInformation>>(`${this.base_path}/shipment`, payload);

    return data;
  }

  async read(id: number | string): Promise<BaseResponse> {
    const url = `${this.base_path}/invoices/${id}/read`;
    const { data } = await this.http_client.patch<BaseResponse>(url);

    return data;
  }
}

export const update_delivery_details = (): Orders.OrderDeliveryUpdated => ({
  delivery_type: null,
  warehouse_id: null,
  transport_company_id: null,
  delivery_address_id: null,
  comment: '',
});

export const default_order_details = (): Orders.OrderDetailedInfo => ({
  id: 0,
  date: '',
  shipment_date: '',
  products: [
    {
      id: 0,
      payment_type: {
        id: 0,
        slug: PaymentMethods.PREPAYMENT,
        name: '',
      },
      product: {
        id: 0,
        nomenclature: '',
        product_code: '',
      },
      prices: {
        price_rub: 0,
        total_price_rub: 0,
        price_nds_rub: 0,
        nds: 0,
      },
      delivery: {
        delivery_type: {
          id: 0,
          slug: DeliveryMethods.PICKUP,
          name: '',
        },
        warehouse: {
          id: 0,
          name: '',
          address: '',
        },
        delivery_address: {
          id: 0,
          address: '',
        },
        transport_company: null,
      },
      quantity: 0,
      reserve_quantity: 0,
      shipment_quantity: 0,
      remnant_quantity: 0,
      volume: '',
      weight: 0,
      shipment_date: '',
      canceled_at: null,
      related_set: null,
    },
  ],

  order_shipment_status: {
    id: 0,
    slug: ShipmentStatuses.NOT_SHIPPED,
    name: '',
  },
  order_status: {
    id: 0,
    slug: OrderStatuses.NEW,
    name: '',
  },
  order_payment_status: {
    id: 0,
    slug: PaymentsStatuses.UNPAID,
    name: '',
  },
  cart_payment_type: {
    id: 0,
    slug: PaymentMethods.PREPAYMENT,
    name: '',
  },
  organization: {
    id: 0,
    name_organization: '',
    is_credit_enabled: false,
    not_final_alfa_api_loan_applications_exists: null,
  },
  atol_organization: {
    id: 0,
    name: '',
    external_id: '',
    recipient_name: '',
    recipient_inn: '',
    recipient_kpp: '',
    created_at: '',
    updated_at: '',
    alfa_api_client: null,
  },
  total_price: null,
  total_volume: null,
  total_weight: null,
  project_id: null,
  contract: null,
  is_allowed_cancel: false,
  is_limit_exceeded: false,
  comment: null,
  register_realization_id: 0,
  delivery_type: null,
  transport_company: null,
  delivery_address: null,
});

export const default_new_order = () => ({
  project_id: null,
  organization: null,
  warehouse: null,
  payment_type: PaymentMethods.PREPAYMENT,
  delivery_type: null,
  products: [],
  delivery_address: null,
  transport_company_id: null,
  self_delivery_type: null,
  tc_delivery_type: null,
  tc_receiving_method: null,
  is_express: false,
  is_dellin: false,
  delline_place: null,
  delline_place_id: null,
  delline_terminal: null,
  delline_terminal_id: null,
  comment: '',
  has_unread_change: false,
  balance_credit_limit: null,
  is_realization_contract: false,
  has_realization_contract: false,
  is_cash_registers_auto_addition: true,
});

/**
 * Получение общего метода оплаты
 * @param { Cart.CartItem[] } items - массив товаров в корзине/заказе
 * @returns { PaymentMethods | string } общий метод оплаты
 */
export const get_common_payment_type = (items: Cart.CartItem[]): PaymentMethods | string => {
  const methods: Record<string, PaymentMethods[]> = {
    [PaymentMethods.PREPAYMENT]: [],
    [PaymentMethods.DEFERMENT]: [],
  };

  items.forEach((item) => {
    if (item.payment_type === PaymentMethods.PREPAYMENT) {
      methods[PaymentMethods.PREPAYMENT].push(item.payment_type);
    } else if (item.payment_type === PaymentMethods.DEFERMENT) {
      methods[PaymentMethods.DEFERMENT].push(item.payment_type);
    }
  });

  if (methods[PaymentMethods.PREPAYMENT].length && methods[PaymentMethods.DEFERMENT].length) {
    return PaymentMethods.DEFERMENT_PREPAYMENT;
  }
  if (methods[PaymentMethods.PREPAYMENT].length && !methods[PaymentMethods.DEFERMENT].length) {
    return PaymentMethods.PREPAYMENT;
  }
  if (!methods[PaymentMethods.PREPAYMENT].length && methods[PaymentMethods.DEFERMENT].length) {
    return PaymentMethods.DEFERMENT;
  }

  return '';
};
